import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Card, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useEffect, useState, useRef } from 'react';
import InfoCard from '@ui/components/InfoCard';
import RecentIncidentCard from '@ui/components/RecentIncidentCard';
import Aside from '@ui/components/Aside';
import useDashboardStyles from './useDashboardStyles';
import { useTranslation } from 'react-i18next';
import { SButtonMenu } from '@ui/components/common';
import Chart from 'react-apexcharts';
import { between } from '@ui/style/style';
import { useDispatch, useSelector } from 'react-redux';
import { clearDashboard, fetchDashboardDetails, fetchDashboardDropDowns, resetAssetId, resetProjectId, setProjectId, setAssetId } from '@application/redux/dashboard/dashboard-slice';
const Dashboard = () => {
    const theme = useTheme();
    const isLightMode = theme.palette.mode === 'light';
    const { t } = useTranslation();
    const { pageHeaderStyles, filterSectionStyles, incidentsGraphStyles, recentIncidentsContainerStyles, leftSectionStyles } = useDashboardStyles();
    const [values, setValues] = useState();
    const dispatch = useDispatch();
    const { dashboard, topIncident, dropDownLists, assetId, projectId, fromDate, toDate } = useSelector((state) => state.dashboardSlice);
    // Track mount state and previous values for change detection
    const isInitialMountRef = useRef(true);
    const prevDepsRef = useRef(null);
    const [isDisabled, setIsDisabled] = useState(!projectId);
    // Serialize dependencies for comparison
    const depsKey = JSON.stringify({ assetId, projectId, fromDate, toDate });
    /**
     * SINGLE useEffect for all dashboard fetching
     * - Initial mount: fetch dashboard + dropdowns
     * - Filter change: refetch dashboard only
     */
    useEffect(() => {
        if (isInitialMountRef.current) {
            // First mount - fetch everything
            isInitialMountRef.current = false;
            prevDepsRef.current = depsKey;
            dispatch(fetchDashboardDetails());
            dispatch(fetchDashboardDropDowns());
        }
        else if (prevDepsRef.current !== depsKey) {
            // Dependencies changed - refetch dashboard
            prevDepsRef.current = depsKey;
            dispatch(fetchDashboardDetails());
        }
        // Update disabled state based on projectId
        setIsDisabled(!projectId);
    }, [depsKey, dispatch, projectId]);
    /**
     * Cleanup on unmount only - empty dependency array
     */
    useEffect(() => {
        return () => {
            dispatch(clearDashboard());
        };
    }, [dispatch]);
    const [selectedProject, setSelectedProject] = useState('All');
    const [selectedAsset, setSelectedAsset] = useState('All');
    const infoCardsData = [
        {
            className: 'active-systems',
            heading: 'Active Systems',
            content: dashboard?.data.basicInfo[0]?.value,
            right: -2,
            focused: true,
            icon: '/assets/svgs/video.svg'
        },
        {
            className: 'total-incidents',
            heading: 'Total Incidents',
            content: dashboard?.data.basicInfo[1]?.value,
            right: -7,
            focused: false,
            icon: '/assets/svgs/clipboard-text.svg'
        },
        {
            className: 'total-systems',
            heading: 'Total Systems',
            content: dashboard?.data.basicInfo[2]?.value,
            right: 5,
            focused: false,
            icon: '/assets/svgs/calendar-video.svg'
        }
    ];
    // Define full month order
    const monthOrder = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
    ];
    const infoMap = (dashboard?.data.barChartInfo || []).reduce((map, item) => {
        map[item.monthName] = item;
        return map;
    }, {});
    const currentYearData = monthOrder.map((m) => infoMap[m]?.incidentCountCurrentYear ?? 0);
    const previousYearData = monthOrder.map((m) => infoMap[m]?.incidentCountPreviousYear ?? 0);
    // Calculate dynamic max for y-axis based on actual data
    const maxDataValue = Math.max(...currentYearData, ...previousYearData, 0);
    const yAxisMax = maxDataValue > 0 ? Math.ceil((maxDataValue * 1.1) / 10) * 10 : 80; // Add 10% padding and round up to nearest 10
    const yAxisTickAmount = Math.min(Math.ceil(yAxisMax / 10), 10); // Dynamic tick amount, max 10 ticks
    const chartOptions = {
        chart: {
            type: 'bar',
            toolbar: { show: false }
        },
        plotOptions: {
            bar: { horizontal: false, columnWidth: '12px', borderRadius: 0 }
        },
        xaxis: {
            categories: [
                t('Jan'),
                t('Feb'),
                t('Mar'),
                t('Apr'),
                t('May'),
                t('Jun'),
                t('Jul'),
                t('Aug'),
                t('Sep'),
                t('Oct'),
                t('Nov'),
                t('Dec')
            ],
            labels: {
                style: {
                    fontSize: '14px',
                    fontFamily: 'General Sans',
                    fontWeight: 400,
                    colors: theme.palette.text.primary
                }
            }
        },
        tooltip: {
            theme: isLightMode ? 'light' : 'dark',
            y: {
                formatter: (value) => `${value} incidents`
            }
        },
        yaxis: {
            min: 0,
            max: yAxisMax,
            tickAmount: yAxisTickAmount,
            labels: {
                formatter: (value) => Math.round(value).toString(),
                style: {
                    fontSize: '14px',
                    fontFamily: 'General Sans',
                    fontWeight: 400,
                    colors: '#454F5B'
                }
            },
            forceNiceScale: true
        },
        fill: { opacity: 1 },
        legend: {
            position: 'top',
            horizontalAlign: 'right',
            offsetX: 40,
            offsetY: 15,
            fontFamily: 'General Sans',
            markers: { shape: 'circle' },
            labels: {
                colors: theme.palette.text.secondary
            }
        },
        // tooltip: {},
        dataLabels: { enabled: false },
        colors: [theme.palette.info.main, theme.palette.warning.main],
        grid: {
            borderColor: '#919EAB33',
            strokeDashArray: 4,
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } }
        }
    };
    const series = [
        { name: t('Current Year'), data: currentYearData },
        { name: t('Previous Year'), data: previousYearData }
    ];
    const handleDateRangeValues = (dates) => {
        setValues(dates);
    };
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { className: 'page__header', sx: pageHeaderStyles, children: [_jsx(Typography, { variant: 'h4', children: t('Dashboard') }), _jsxs(Box, { className: 'filters-section', sx: filterSectionStyles, children: [_jsx(SButtonMenu, { isDisabled: false, label: 'Project:', options: [
                                    { name: 'All', value: 'All' },
                                    ...dropDownLists.projects.map((p) => ({ name: p.name, value: p.id }))
                                ], value: selectedProject, handleChange: (opt) => {
                                    setSelectedAsset('All');
                                    dispatch(resetAssetId());
                                    if (opt === 'All') {
                                        dispatch(resetProjectId());
                                        setSelectedProject('All');
                                        setIsDisabled(true);
                                    }
                                    else {
                                        dispatch(setProjectId(opt));
                                        setSelectedProject(dropDownLists.projects.find((p) => p.id === opt)?.name || '');
                                        setIsDisabled(false);
                                    }
                                } }), _jsx(SButtonMenu, { isDisabled: isDisabled, label: 'Asset:', options: [
                                    { name: 'All', value: 'All' },
                                    ...dropDownLists.assets.map((p) => ({ name: p.name, value: p.id }))
                                ], value: selectedAsset, handleChange: (opt) => {
                                    if (opt === 'All') {
                                        dispatch(resetAssetId());
                                        setSelectedAsset('All');
                                    }
                                    else {
                                        dispatch(setAssetId(opt));
                                        setSelectedAsset(dropDownLists.assets.find((p) => p.id === opt)?.name || '');
                                    }
                                } })] })] }), _jsxs(Grid, { container: true, spacing: 3, className: 'main-grid', children: [_jsxs(Grid, { className: 'left-section', size: 9, rowSpacing: 3, sx: leftSectionStyles, children: [_jsx(Grid, { container: true, spacing: 3, children: infoCardsData.map(({ className, content, focused, heading, icon, right }, idx) => (_jsx(Grid, { size: 4, children: _jsx(InfoCard, { className: className, heading: heading, content: content, right: right, focused: focused, icon: icon }) }, idx))) }), _jsx(Grid, { container: true, className: 'monthly-incidents-graph-grid', sx: { mt: 3 }, children: _jsx(Grid, { size: 12, children: _jsxs(Card, { className: 'monthly-incidents-graph', sx: incidentsGraphStyles, children: [_jsx(Chart, { options: chartOptions, series: series, type: 'bar', height: 286 }), _jsx(Box, { sx: {
                                                    position: 'absolute',
                                                    top: 20,
                                                    left: 0,
                                                    px: 2,
                                                    zIndex: 50,
                                                    ...between,
                                                    gap: 1
                                                }, children: _jsx(Typography, { variant: 'subtitle1', color: theme.palette.text.primary, children: t('Incidents per Month') }) })] }) }) }), _jsx(Grid, { container: true, className: 'recent-incidents-grid', sx: { mt: 3 }, children: _jsx(Grid, { size: 12, children: _jsxs(Box, { className: 'recent-incidents-container', sx: recentIncidentsContainerStyles, children: [_jsx(Typography, { variant: 'subtitle1', color: 'textPrimary', children: t('RecentIncidents') }), _jsx(Grid, { container: true, spacing: 2, children: dashboard?.data.last5MostRecentRecords.map((item) => {
                                                    return (_jsx(Grid, { size: 6, children: _jsx(RecentIncidentCard, { record: item }) }, item.id));
                                                }) })] }) }) })] }), _jsx(Grid, { className: 'right-section-grid', size: 3, children: _jsx(Aside, { data: dashboard?.data.last10RecordsWithHeighScore, title: 'Top Incidents by Score' }) })] })] }));
};
export default Dashboard;
